import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import * as schema from '@bulknutrients/bulk-shared-lib/lib/drizzle/forms/schema';
import { drizzle } from '@bulknutrients/bulk-shared-lib/lib/drizzle/instances';
import { getReviewCount } from '@bulknutrients/bulk-shared-lib/lib/postgres/forms';
import {
  type FlavourCampaignPageData,
  getCampaignPageData,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/campaigns';
import { cdnClient } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { getFPTProducts } from '@bulknutrients/bulk-shared-lib/lib/sanity/productData';
import { addBigCommerceDataToBaseProducts } from '@bulknutrients/bulk-shared-lib/lib/sanity/products/transform';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { EmblaCarousel } from '@components/Carousels/Carousel';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { CustomImage } from '@components/Media/Image';
import { Video } from '@components/Media/Video/Video';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { VimeoRenderer } from '@components/PortableText/VideoRenderers';
import { StarRating } from '@components/Product/StarRating';
import {
  ProductCardFlavourCampaign,
  ProductCardVerticalVariant,
} from '@components/ProductCard/ProductCard';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { log } from '@lib/logs';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { Pool } from 'pg';
import { type ReactElement, type ReactNode, useEffect } from 'react';
import { ReviewCard } from '..';

interface Props {
  pageData: FlavourCampaignPageData;
  fptProducts: BaseProduct[];
  reviewCount: number;
  isPreview: boolean;
}

export default function FlavourCampaignPage({
  pageData,
  fptProducts,
  reviewCount,
  isPreview,
}: Props): ReactElement {
  const { setRecentlyViewedPage } = useLocalStorage();

  const RendererOverride = {
    block: {
      h1: ({ children }: { children?: ReactNode }) => (
        <>
          {/* Has to be with style as it is dynamic so tailwind doesn't read it */}
          <h1
            style={{ color: pageData.primaryColor }}
            className="not-italic uppercase text-left tracking-normal text-4xl"
          >
            {children}
          </h1>
        </>
      ),
    },
  };

  useEffect(() => {
    setRecentlyViewedPage({
      page: { slug: pageData.slug, title: pageData.name },
      viewedTime: Date.now(),
    });
  }, [pageData, setRecentlyViewedPage]);

  return (
    <Container as="main" id="main-content">
      <CustomImage image={pageData.image} width={2500} />

      <PortableTextBlockRenderer
        content={pageData.description}
        componentsOverride={RendererOverride}
      />

      <EmblaCarousel
        slides={pageData.products.map((product) => (
          <ProductCardFlavourCampaign
            key={product.entityId}
            product={product}
          />
        ))}
      />

      {pageData.video && pageData.video._type === 'vimeoVideo' && (
        <VimeoRenderer
          videoId={pageData.video.videoId}
          altText={pageData.video.altText}
        />
      )}
      {pageData.video && pageData.video._type === 'muxVideo' && (
        <Video
          playbackId={pageData.video.playbackId}
          altText={pageData.video.altText}
          placeholder={pageData.video.placeholder}
          aspectRatio={pageData.video.aspectRatio}
        />
      )}

      {/* CUSTOMER/AMBASSADOR REVIEW CAROUSEL */}
      <div className="full-bleed mt-4 py-6 pl-6 bg-grey-light dark:bg-grey-darkest flex flex-col gap-4">
        <StarRating rating={5} size={30} className="gap-2 self-center" />
        <div className="font-extrabold uppercase text-center text-orange text-3xl pr-6">
          <span className="text-black dark:text-white">
            over {reviewCount.toLocaleString()}
          </span>{' '}
          5 star ratings & reviews
        </div>
        <EmblaCarousel
          slides={pageData.reviews.map((r) => {
            return <ReviewCard key={`review_pos${r.body}`} review={r} />;
          })}
        />
      </div>

      {fptProducts.length > 0 && (
        <section className="full-bleed px-5 py-6">
          <EmblaCarousel
            title="You might also like"
            slides={fptProducts.map((product) => (
              <ProductCardVerticalVariant
                key={product.entityId}
                product={product}
              />
            ))}
          />
        </section>
      )}
    </Container>
  );
}

FlavourCampaignPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const data: string[] = await cdnClient.fetch(
    `*[_type == "flavourCampaign" && defined(slug.current)].slug.current`
  );

  const paths = data.map((slug) => ({
    params: { slug: slug.replace(/^\/flavours\//, '').replace(/^\/+/, '') },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  try {
    let pageData: FlavourCampaignPageData | null;

    if (preview) {
      // This is preview mode
      pageData = await getCampaignPageData(`/flavours/${params?.slug}`, true);
    } else {
      pageData = await getCampaignPageData(`/flavours/${params?.slug}`);
    }

    if (!pageData) {
      // try redirect
      const redirect = await getRedirect(
        `/flavours/${params?.slug?.toString().split(',').join('/')}` as string
      );
      if (redirect) {
        return {
          redirect: {
            destination: redirect.newUrl,
            permanent: redirect.permanent,
          },
        };
      }

      // fallback failed
      return {
        notFound: true,
      };
    }

    if (pageData.description) {
      // If there is a product carousel, add the BC data that is needed for a BaseProduct
      // Get all products out
      const products = [] as Array<any>;
      for (const content of pageData.description) {
        if (
          content._type === 'carousel' &&
          content.items[0]._type === 'product'
        ) {
          products.push(...content.items);
        }
      }

      // Map products
      const mappedProducts = await addBigCommerceDataToBaseProducts(products);

      // Replace them
      pageData.description = pageData.description.map((content) => {
        if (
          content._type === 'carousel' &&
          content.items[0]._type === 'product'
        ) {
          const mappedItems = content.items.map((item) =>
            mappedProducts.find((p) => p.entityId === item.entityId)
          );
          return {
            ...content,
            items: mappedItems,
          };
        }
        return content;
      });
    }

    // Get FPT products for the "You might also like" section
    const fptProducts = await Promise.all(
      pageData.products.map(async (product) =>
        getFPTProducts(product.entityId).catch((error) => {
          log({
            error,
            location: `getStaticProps fptProducts /flavours/${params?.slug?.toString()}`,
          });
          return [] as BaseProduct[];
        })
      )
    )
      .then((products) =>
        products
          .flat()
          .filter(
            (p) =>
              !pageData.products
                .map((prod) => prod.entityId)
                .includes(p.entityId)
          )
      )
      .catch((error) => {
        log({
          error,
          location: `getStaticProps fptProducts /flavours/${params?.slug?.toString()}`,
        });
        return [] as BaseProduct[];
      });

    // Count of all reviews
    if (
      !process.env.PGUSER ||
      !process.env.PGPASSWORD_FORMS ||
      !process.env.PGDATABASE_FORMS ||
      !process.env.PGHOST_FORMS
    ) {
      throw new Error('Database environment variables are not set');
    }
    const pool = new Pool({
      user: process.env.PGUSER,
      password: process.env.PGPASSWORD_FORMS,
      database: process.env.PGDATABASE_FORMS,
      host: process.env.PGHOST_FORMS,
      port: 5432,
    });
    const db = drizzle(pool, { schema });

    const { count } = await getReviewCount({
      db,
      stars: '5',
    });

    return {
      props: {
        pageData,
        fptProducts,
        reviewCount: count,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    log({ error });
    throw error;
  }
};
